import React from 'react';
import type { Recommendation } from '../types';
import { RecommendationCard } from './RecommendationCard';
import { TradeDetailModal } from './TradeDetailModal';

interface RecommendationListProps {
  recommendations: Recommendation[];
  expandedIds: Set<number>;
  loading: boolean;
  error: string | null;
  hasQuery: boolean;
  onToggleExpand: (id: number) => void;
  onRetry: () => void;
}

export function RecommendationList({
  recommendations,
  expandedIds,
  loading,
  error,
  hasQuery,
  onToggleExpand,
  onRetry
}: RecommendationListProps) {
  const expandedId = expandedIds.size > 0 ? Array.from(expandedIds)[0] : null;
  const expandedRec = expandedId != null ? recommendations.find((r) => r.id === expandedId) : null;

  return (
    <div className="content-section">
      {!hasQuery && (
        <div className="section-header">
          <h2>Trading Signals</h2>
          {recommendations.length > 0 && (
            <span className="count-badge">{recommendations.length}</span>
          )}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div>
            <strong>Error</strong>
            <p>{error}</p>
          </div>
          <button className="btn-sm" onClick={onRetry}>Retry</button>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner-large"></div>
          <p>Loading recommendations...</p>
        </div>
      ) : recommendations.length === 0 ? (
        <div className="empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 20V4h18v16H3z" />
            <path d="M6 14h12" strokeDasharray="3 2" opacity="0.7" />
          </svg>
          <h3>No signals found</h3>
          <p>Try adjusting your filters to see more results</p>
        </div>
      ) : (
        <div className="cards-grid">
          {recommendations.map((rec) => (
            <RecommendationCard
              key={rec.id}
              rec={rec}
              isExpanded={expandedIds.has(rec.id)}
              onToggleExpand={() => onToggleExpand(rec.id)}
            />
          ))}
        </div>
      )}

      {expandedRec && (
        <TradeDetailModal rec={expandedRec} onClose={() => onToggleExpand(expandedRec.id)} />
      )}
    </div>
  );
}
